/**
 * Klaviyo API Client (Server-Side Only)
 *
 * This client handles all server-side interactions with the Klaviyo REST API.
 * It provides methods for:
 * - Creating and updating user profiles
 * - Subscribing users to email lists (with double opt-in support)
 * - Combined profile creation + list subscription workflow
 *
 * Environment Variables Required:
 * - KLAVIYO_PRIVATE_API_KEY: Your Klaviyo Private API Key
 *
 * @example
 * ```ts
 * const client = getKlaviyoClient();
 * await client.createProfileAndSubscribe(
 *   'list-id',
 *   'user@example.com',
 *   { firstName: 'John', lastName: 'Doe' }
 * );
 * ```
 */
import type {
  KlaviyoCreateProfilePayload,
  KlaviyoSubscribeToListPayload,
  KlaviyoTrackEventPayload,
  KlaviyoErrorResponse,
  KlaviyoSuccessResponse,
} from './types';

const KLAVIYO_API_URL = 'https://a.klaviyo.com/api';
const KLAVIYO_API_VERSION = '2024-10-15';

export class KlaviyoClient {
  private apiKey: string;

  constructor(apiKey: string) {
    if (!apiKey) {
      throw new Error('Klaviyo API key is required');
    }
    this.apiKey = apiKey;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T | null> {
    const url = `${KLAVIYO_API_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Klaviyo-API-Key ${this.apiKey}`,
        revision: KLAVIYO_API_VERSION,
        ...options.headers,
      },
    });

    // Get response text first
    const text = await response.text();

    // Try to parse as JSON if there's content
    let data: any = null;
    if (text && text.length > 0) {
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse response as JSON:', text);
      }
    }

    // Log detailed info for debugging
    console.log('Klaviyo API Request:', {
      endpoint,
      method: options.method || 'GET',
      status: response.status,
      statusText: response.statusText,
      hasBody: !!text,
      bodyLength: text?.length || 0,
    });

    if (!response.ok) {
      const error = data as KlaviyoErrorResponse;
      console.error('Klaviyo API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        error: error,
        rawResponse: text,
      });

      const errorMessage =
        error?.errors?.[0]?.detail ||
        error?.errors?.[0]?.title ||
        `Klaviyo API request failed with status ${response.status}: ${response.statusText}`;

      throw new Error(errorMessage);
    }

    return data as T;
  }

  /**
   * Create or update a profile in Klaviyo (upsert operation)
   * This endpoint will create a new profile or update an existing one based on email/phone
   * https://developers.klaviyo.com/en/reference/create_or_update_profile
   */
  async createOrUpdateProfile(payload: KlaviyoCreateProfilePayload) {
    return this.request<KlaviyoSuccessResponse>('/profile-import/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Create a new profile in Klaviyo (will fail if profile already exists)
   */
  async createProfile(payload: KlaviyoCreateProfilePayload) {
    return this.request<KlaviyoSuccessResponse>('/profiles/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Subscribe a profile to a list with double opt-in
   * When double opt-in is enabled on the list, this will trigger the confirmation email
   * Note: This endpoint only accepts email, phone_number, and subscriptions
   * For profile metadata (name, properties), use createProfile first
   */
  async subscribeToList(
    listId: string,
    email: string,
    phoneNumber?: string,
  ): Promise<any> {
    const payload: KlaviyoSubscribeToListPayload = {
      data: {
        type: 'profile-subscription-bulk-create-job',
        attributes: {
          profiles: {
            data: [
              {
                type: 'profile',
                attributes: {
                  email,
                  ...(phoneNumber && { phone_number: phoneNumber }),
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED',
                      },
                    },
                  },
                },
              },
            ],
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    // This endpoint returns 202 Accepted with or without a body
    await this.request('/profile-subscription-bulk-create-jobs/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    // Return success response
    return { success: true };
  }

  /**
   * Create/update profile with custom properties and subscribe to list
   * Two-step process:
   * 1. Create or update profile with all metadata (name, properties) - uses upsert
   * 2. Subscribe to list (triggers double opt-in email if enabled)
   */
  async createProfileAndSubscribe(
    listId: string,
    email: string,
    options?: {
      anonymousId?: string; // For profile merge
      phoneNumber?: string;
      firstName?: string;
      lastName?: string;
      properties?: Record<string, unknown>;
    },
  ): Promise<any> {
    // Step 1: Create or update the profile with all details (upsert)
    // Include anonymous_id to merge with anonymous profile
    await this.createOrUpdateProfile({
      data: {
        type: 'profile',
        attributes: {
          email,
          // CRITICAL: Include anonymous_id for profile merge
          ...(options?.anonymousId && { anonymous_id: options.anonymousId }),
          ...(options?.phoneNumber && { phone_number: options.phoneNumber }),
          ...(options?.firstName && { first_name: options.firstName }),
          ...(options?.lastName && { last_name: options.lastName }),
          ...(options?.properties && { properties: options.properties }),
        },
      },
    });

    // Step 2: Subscribe to the list (this triggers double opt-in email if enabled)
    return this.subscribeToList(listId, email, options?.phoneNumber);
  }

  /**
   * Track an event for an anonymous visitor
   * Uses the anonymous_id to track events before the user is identified
   * These events will be backfilled to the profile once the user is identified (up to 14 days)
   * https://developers.klaviyo.com/en/reference/create_event
   */
  async trackAnonymousEvent(
    anonymousId: string,
    eventName: string,
    options?: {
      properties?: Record<string, unknown>;
      value?: number;
      time?: Date;
    },
  ): Promise<any> {
    const payload = {
      data: {
        type: 'event',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                anonymous_id: anonymousId,
              },
            },
          },
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: eventName,
              },
            },
          },
          ...(options?.properties && { properties: options.properties }),
          ...(options?.value && { value: options.value }),
          ...(options?.time && { time: options.time.toISOString() }),
        },
      },
    };

    return this.request('/events/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Track an event for a user
   * This is used to trigger flows and track user behavior
   * Events like "Started Checkout", "Placed Order", etc. are used as flow triggers
   * https://developers.klaviyo.com/en/reference/create_event
   */
  async trackEvent(
    email: string,
    eventName: string,
    options?: {
      phoneNumber?: string;
      firstName?: string;
      lastName?: string;
      properties?: Record<string, unknown>;
      value?: number;
      time?: Date;
    },
  ): Promise<any> {
    const payload: KlaviyoTrackEventPayload = {
      data: {
        type: 'event',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                ...(options?.phoneNumber && {
                  phone_number: options.phoneNumber,
                }),
                ...(options?.firstName && { first_name: options.firstName }),
                ...(options?.lastName && { last_name: options.lastName }),
              },
            },
          },
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: eventName,
              },
            },
          },
          properties: {
            ...options?.properties,
            ...(options?.value !== undefined && { $value: options.value }),
          },
          time: options?.time?.toISOString() || new Date().toISOString(),
        },
      },
    };

    return this.request<KlaviyoSuccessResponse>('/events/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

// Singleton instance
let klaviyoClient: KlaviyoClient | null = null;

export function getKlaviyoClient(): KlaviyoClient {
  if (!klaviyoClient) {
    const apiKey = process.env.KLAVIYO_PRIVATE_API_KEY;
    if (!apiKey) {
      throw new Error('KLAVIYO_PRIVATE_API_KEY is not set in environment');
    }
    klaviyoClient = new KlaviyoClient(apiKey);
  }
  return klaviyoClient;
}
